import {
    I_SearchPriceData,
    I_FlightInfos,
    I_SearchFilter,
    I_Filter,
    I_ItineraryInfos,
    I_FareInfos,
    I_SegmentInfos,
    I_SegmentDetailInfos,
} from '@Redux/search/interfaces';
import {
    I_APIFilterItemData,
    I_departureTimesRange,
    I_FilterItemList,
    I_FilterItem,
} from './interface';

import { HotCitySort } from './HotCitySort.json';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// 根據 HotCitySort 取得排序物件
interface I_hotCity {
    seqNo: string;
    value: string;
}
const getSortHotCity = (HotCitySort: I_hotCity[]) => {
    if (HotCitySort.length === 0) return [];

    const sortedHotCity = HotCitySort.sort(
        (city1, city2) => Number(city1.seqNo) - Number(city2.seqNo)
    ).map((hotCity) => hotCity.value.split('-').pop());

    return sortedHotCity;
};

/** @summary 根據城市排序機場
 * @param sortBaseArr 已有排序的陣列
 * @param arr 需要排序的物件陣列 (本身已為 unique)
 * @param key 排序的依據 key
 * @returns 排序完成的陣列
 */
const sortProductAreaID = (sortBaseArr: any[], arr: any[], key: string) => {
    if (!arr || arr.length === 0 || !sortBaseArr || sortBaseArr.length === 0) {
        return [];
    }

    const includedInBase: any[] = [];
    const notInBase: any[] = [];

    arr.forEach((item) => {
        if (sortBaseArr.includes(item[key])) {
            includedInBase.push(item);
        } else {
            notInBase.push(item);
        }
    });

    includedInBase.sort((a: any, b: any) => {
        return sortBaseArr.indexOf(a[key]) - sortBaseArr.indexOf(b[key]);
    });

    return includedInBase.concat(notInBase);
};

/** @summary 對 filterItems 進行排序
 * @param filterItems 計算出的 (未排序) 篩選欄位
 * @returns 排序後的篩選欄位
 */
const sortFilterItems = (filterItems: I_FilterItemList) => {
    const sortedFilterItems = JSON.parse(JSON.stringify(filterItems)) as I_FilterItemList;

    // 轉機根據次數排序 transfer
    if (sortedFilterItems.transfer.length > 0) {
        sortedFilterItems.transfer = sortedFilterItems.transfer.sort(
            (a: { value: any }, b: { value: any }) => a.value - b.value
        );
    }

    // 航空公司根據價格排序 (airport)
    if (sortedFilterItems.airport.length > 0) {
        sortedFilterItems.airport = sortedFilterItems.airport.sort(
            // 用最低價格排序
            (a, b) => (a.price && b.price ? a.price - b.price : -1)

            // 用 A-Z 排序
            // (a: { value: any }, b: { value: any }) => {
            //     if (a.value.charAt(0) > b.value.charAt(0)) {
            //         return 1;
            //     } else {
            //         return -1;
            //     }
            // }
        );
    }

    // 機場根據城市熱門順序排序 seqAirport (departure & arrival) & transferPoint
    const sortHotCity = getSortHotCity(HotCitySort);

    if (sortedFilterItems.seqAirport.length > 0) {
        sortedFilterItems.seqAirport.forEach((seq, i) => {
            sortedFilterItems.seqAirport[i].departure =
                sortHotCity && sortedFilterItems.seqAirport[i].departure
                    ? sortProductAreaID(
                          sortHotCity,
                          sortedFilterItems.seqAirport[i].departure,
                          'city'
                      )
                    : sortedFilterItems.seqAirport[i].departure;

            sortedFilterItems.seqAirport[i].arrival =
                sortHotCity && sortedFilterItems.seqAirport[i].arrival
                    ? sortProductAreaID(
                          sortHotCity,
                          sortedFilterItems.seqAirport[i].arrival,
                          'city'
                      )
                    : sortedFilterItems.seqAirport[i].arrival;
        });
    }

    if (sortedFilterItems.transferPoint.length > 0) {
        sortedFilterItems.transferPoint =
            sortHotCity && sortedFilterItems.transferPoint
                ? sortProductAreaID(sortHotCity, sortedFilterItems.transferPoint, 'city')
                : sortedFilterItems.transferPoint;
    }

    // 禮品排序 Gifts (有禮品/無禮品)
    if (sortedFilterItems.Gifts) {
        const sortArr: any = [];
        sortedFilterItems.Gifts.forEach((c: any) => {
            if (c.value == true) sortArr.push(c);
        });
        sortedFilterItems.Gifts.forEach((c: any) => {
            if (c.value != true) sortArr.push(c);
        });
        sortedFilterItems.Gifts = sortArr;
    }

    return sortedFilterItems;
};

/** @summary 藉由 API 取得篩選項目計算出欲顯示初始篩選欄位
 * @param filterItemData 從後端 API 取得的篩選資料
 * @returns 篩選欄位
 */
export const initializeFilterItems = (filterItemData: I_APIFilterItemData) => {
    const filterItems: I_FilterItemList = {
        transfer: [],
        airport: [],
        crossday: [],
        departureTimesRanges: [],
        seqAirport: [],
        transferPoint: [],
        Gifts: [],
        Cabin: [],
    };

    const {
        StopNum,
        Airline,
        HaveCrossDay,
        AirPortTime,
        AirPort,
        StopAirport,
        Cabin,
        HaveGfno,
        // PriceRange,
    } = filterItemData;

    // 轉機次數 transfer
    StopNum.forEach((transferCount) => {
        if (transferCount === 0) {
            filterItems.transfer.push({ text: '直飛(含中停)', value: 0 });
        } else {
            filterItems.transfer.push({
                text: '轉機' + transferCount + '次',
                value: transferCount,
            });
        }
    });

    // 航空聯盟、航空公司 airport
    Airline.forEach((airlineInfo) => {
        filterItems.airport.push({
            text: airlineInfo.ValidatingAirlineName,
            value: airlineInfo.ValidatingAirline,
            price: airlineInfo.MinPrice,
        });
    });

    // 跨日航班 (都是跨日則不顯示)
    if (HaveCrossDay && HaveCrossDay[0].HaveCrossDay) {
        filterItems.crossday.push({
            text: '排除跨日航班',
            value: 0,
        });
    }

    // 出發時間 departureTimesRanges [單程 (1筆) 、來回 (2筆)、多趟 (>=兩筆)]
    AirPortTime.forEach((timeRange) => {
        filterItems.departureTimesRanges.push({
            F_startTime: timeRange.DepHourMin,
            F_endTime: timeRange.DepHourMax,
        });
    });

    // 航班之出發 & 抵達機場
    AirPort.forEach((seqAirports) => {
        const departure = seqAirports.DepAirport.map((airport) => ({
            text: airport.AirportName,
            value: airport.Airport,
            city: airport.AirportCity,
        }));
        const arrival = seqAirports.ArrAirport.map((airport) => ({
            text: airport.AirportName,
            value: airport.Airport,
            city: airport.AirportCity,
        }));
        filterItems.seqAirport.push({
            seqNum: seqAirports.SeqNo,
            departure,
            arrival,
        });
    });

    // 轉機點 transferPoint
    StopAirport.forEach((airport) => {
        filterItems.transferPoint.push({
            text: airport.AirportName,
            value: airport.Airport,
            city: airport.AirportCity,
        });
    });

    // 禮品 Gifts
    if (HaveGfno) {
        HaveGfno.forEach((gfNo) => {
            if (gfNo.HaveGfno) {
                filterItems.Gifts.push({
                    text: '有禮品',
                    value: true,
                });
            } else {
                filterItems.Gifts.push({
                    text: '無禮品',
                    value: null,
                });
            }
        });
    }

    // 艙等 Cabin
    Cabin.forEach((cabinType) => {
        filterItems.Cabin.push({
            text: cabinType.CabinName,
            value: cabinType.Cabin,
        });
    });

    const sortedFilterItems = sortFilterItems(filterItems);
    return sortedFilterItems;
};

/** @summary 根據當前顯示的機票資料計算出篩選欄位
 * @param showData 當前顯示的資料
 * @returns 篩選欄位
 */
export const updateFilterItems = (showData: I_SearchPriceData) => {
    const filterItems: I_FilterItemList = {
        transfer: [],
        airport: [],
        seqAirport: [],
        transferPoint: [],
        Cabin: [],
        departureTimesRanges: [],
        crossday: [],
        Gifts: [],
    };

    // let Airlines_lowestPrice: { [key: string]: number } = {};

    const timeRanges: I_departureTimesRange[] = [];

    const crossdayArr: boolean[] = [];

    // 用每一筆的機票卡片資料做計算
    showData.FlightInfos.forEach((flight: I_FlightInfos) => {
        // 轉機次數 transfer (交給多選處理)
        // flight.ItineraryInfos.forEach((itinerary) => {
        //     if (
        //         filterItems.transfer.filter((filter) => filter.value === itinerary.Transfer)
        //             .length > 0
        //     )
        //         return;

        //     if (
        //         itinerary.Transfer > 0 &&
        //         flight.ItineraryInfos.every((filter) => filter.Transfer < itinerary.Transfer + 1)
        //     ) {
        //         filterItems.transfer.push({
        //             text: '轉機' + itinerary.Transfer + '次',
        //             value: itinerary.Transfer,
        //         });
        //     }

        //     if (
        //         itinerary.Transfer === 0 &&
        //         flight.ItineraryInfos.every((filter) => filter.Transfer === 0)
        //     ) {
        //         filterItems.transfer.push({
        //             text: '直飛(含中停)',
        //             value: 0,
        //         });
        //     }
        // });

        // 航空聯盟、航空公司 airport
        // flight.FareInfos.forEach((fare) => {
        //     if (!Airlines_lowestPrice[flight.ValidatingAirline]) {
        //         Airlines_lowestPrice[flight.ValidatingAirline] = fare.AdtPrice;
        //     }

        //     if (Airlines_lowestPrice[flight.ValidatingAirline] > fare.AdtPrice) {
        //         Airlines_lowestPrice[flight.ValidatingAirline] = fare.AdtPrice;
        //     }
        // });
        // if (
        //     filterItems.airport.filter((filter) => filter.value === flight.ValidatingAirline)
        //         .length === 0
        // ) {
        //     filterItems.airport.push({
        //         text: flight.ValidatingAirlineName,
        //         value: flight.ValidatingAirline,
        //         price: Math.ceil(Airlines_lowestPrice[flight.ValidatingAirline]) || 0,
        //     });
        // }

        // 是否為跨日 (若有航班的 CrossDay > 0 的情況)
        if (flight.ItineraryInfos.some((c: I_ItineraryInfos) => c.CrossDay >= 1)) {
            crossdayArr.push(true);
            if (filterItems.crossday.filter((filter) => filter.value === 0).length === 0) {
                filterItems.crossday.push({
                    text: '排除跨日航班',
                    value: 0,
                });
            }
        } else {
            crossdayArr.push(false);
        }

        // 出發時間 departureTime (單程、來回、多趟) => 取最早最晚
        flight.ItineraryInfos.forEach((itinerary, i) => {
            if (!timeRanges[i]) {
                timeRanges[i] = {
                    F_startTime: itinerary.DepDateTimeHour,
                    F_endTime: itinerary.DepDateTimeHour,
                };
            }

            if (timeRanges[i].F_startTime > itinerary.DepDateTimeHour) {
                timeRanges[i].F_startTime = itinerary.DepDateTimeHour;
            }

            if (timeRanges[i].F_endTime < itinerary.DepDateTimeHour) {
                timeRanges[i].F_endTime = itinerary.ArrDateTimeHour;
            }
        });

        // 初始化多航段
        flight.ItineraryInfos.forEach((itinerary) => {
            if (filterItems.seqAirport.find((seq) => seq.seqNum === itinerary.SeqNo)) return;

            filterItems.seqAirport.push({
                seqNum: itinerary.SeqNo,
                departure: [],
                arrival: [],
            });
        });

        // 不同航段的出發機場 & 抵達機場 seqAirport
        flight.ItineraryInfos.forEach((itinerary) => {
            const seqObj = filterItems.seqAirport.find((seq) => seq.seqNum === itinerary.SeqNo);

            if (
                seqObj?.departure.filter((filter) => filter.value === itinerary.DepAirport)
                    .length === 0
            ) {
                filterItems.seqAirport[seqObj.seqNum - 1].departure.push({
                    text: itinerary.DepAirportName,
                    value: itinerary.DepAirport,
                    city: itinerary.DepCity,
                });
            }

            if (
                seqObj?.arrival.filter((filter) => filter.value === itinerary.ArrAirport).length ===
                0
            ) {
                filterItems.seqAirport[seqObj.seqNum - 1].arrival.push({
                    text: itinerary.ArrAirportName,
                    value: itinerary.ArrAirport,
                    city: itinerary.ArrCity,
                });
            }
        });

        // 轉機點 transferPoint
        flight.ItineraryInfos.forEach((itinerary) => {
            if (itinerary.SegmentInfos.length <= 1) return;

            itinerary.SegmentInfos.forEach((segment, i) => {
                if (
                    filterItems.transferPoint.filter(
                        (tPoint) => tPoint.value === segment.ArrAirport
                    ).length === 0 &&
                    i < itinerary.SegmentInfos.length - 1
                ) {
                    filterItems.transferPoint.push({
                        text: segment.ArrAirportName,
                        value: segment.ArrAirport,
                        city: segment.ArrCity,
                    });
                }
            });
        });

        // 禮品 Gifts / 艙等 Cabin (只取最低價方案選項 (結果頁顯示的方案))
        const minPriceFare = flight.FareInfos[0];
        // Gifts
        if (
            filterItems.Gifts.filter(
                (filter: any) => Boolean(filter.value) === Boolean(minPriceFare.GiftNo)
            ).length === 0
        ) {
            if (minPriceFare.GiftNo) {
                filterItems.Gifts.push({
                    text: '有禮品',
                    value: true,
                });
            } else {
                filterItems.Gifts.push({
                    text: '無禮品',
                    value: null,
                });
            }
        }
        // Cabin
        minPriceFare.SegmentDetailInfos.forEach((segment) => {
            if (!segment.Cabin) return;

            if (filterItems.Cabin.filter((filter) => filter.value === segment.Cabin).length === 0) {
                filterItems.Cabin.push({
                    text: segment.CabinName,
                    value: segment.Cabin,
                });
            }
        });

        // 過去 - 比對套裝方案中的所有方案
        // flight.FareInfos.forEach((fare) => {
        //     fare.SegmentDetailInfos.forEach((segment) => {
        //         if (!segment.Cabin) return;
        //         if (
        //             filterItems.Cabin.filter((filter) => filter.value === segment.Cabin).length ===
        //             0
        //         ) {
        //             filterItems.Cabin.push({
        //                 text: segment.CabinName,
        //                 value: segment.Cabin,
        //             });
        //         }
        //     });
        // });
    });

    // 如果全部的航班都有跨日，就不要顯示 『排除跨日航班』 的選項
    if (crossdayArr.every((c) => c === true)) {
        if (filterItems.crossday.filter((filter) => filter.value === 0).length !== 0) {
            filterItems.crossday = [];
        }
    }

    filterItems.departureTimesRanges = timeRanges;

    // 只是要拿來比對用，不用再進行排序
    // const sortedFilterItems = sortFilterItems(filterItems);

    return filterItems;
};

/** @summary 取得單一多選的欄位
 * @param filterData 篩選條件
 * @param searchData 初始搜索結果
 * @param filterItem 多選的欄位名稱
 * @returns 單筆多選的篩選欄位
 */
export const getMultiSelectItem = (filterData: any, searchData: any, filterItem: string) => {
    const noMultiSelectFilter: any = {} as I_SearchFilter;
    Object.keys(filterData)
        .filter((key) => key !== filterItem)
        .forEach((key) => {
            noMultiSelectFilter[key] = filterData[key];
        });

    const newShowDataFilter = filterSearchResult(
        noMultiSelectFilter,
        searchData
    ) as I_SearchPriceData;

    const resultItem: any = [];

    const Airlines_lowestPrice: { [key: string]: number } = {};

    // 用每一筆的機票卡片資料做計算 (多選欄位判斷加在這邊)
    newShowDataFilter.FlightInfos.forEach((flight: I_FlightInfos) => {
        // 轉機次數 transfer
        if (filterItem === 'transfer') {
            flight.ItineraryInfos.forEach((itinerary) => {
                if (
                    resultItem.filter((filter: any) => filter.value === itinerary.Transfer).length >
                    0
                )
                    return;

                if (
                    itinerary.Transfer > 0 &&
                    flight.ItineraryInfos.every(
                        (filter) => filter.Transfer < itinerary.Transfer + 1
                    )
                ) {
                    resultItem.push({
                        text: '轉機' + itinerary.Transfer + '次',
                        value: itinerary.Transfer,
                    });
                }

                if (
                    itinerary.Transfer === 0 &&
                    flight.ItineraryInfos.every((filter) => filter.Transfer === 0)
                ) {
                    resultItem.push({
                        text: '直飛(含中停)',
                        value: 0,
                    });
                }
            });
        }

        // 航空聯盟、航空公司 airport
        // TODO 目前搜尋結果頁只會顯示方案裝的最低價方案 (可以不用都檢查)
        if (filterItem === 'airport') {
            flight.FareInfos.forEach((fare) => {
                if (!Airlines_lowestPrice[flight.ValidatingAirline]) {
                    Airlines_lowestPrice[flight.ValidatingAirline] = fare.AdtPrice;
                }

                if (Airlines_lowestPrice[flight.ValidatingAirline] > fare.AdtPrice) {
                    Airlines_lowestPrice[flight.ValidatingAirline] = fare.AdtPrice;
                }
            });
            if (
                resultItem.filter((filter: any) => filter.value === flight.ValidatingAirline)
                    .length === 0
            ) {
                resultItem.push({
                    text: flight.ValidatingAirlineName,
                    value: flight.ValidatingAirline,
                    price: Math.ceil(Airlines_lowestPrice[flight.ValidatingAirline]) || 0,
                });
            }
        }
    });

    return resultItem;
};

/** @summary 比對初始篩選欄位和依篩選後資料計算出的篩選欄位進行 disable
 * @param initialFilterData 初始篩選欄位 (API來的)
 * @param showDataFilter 當前結果計算出的篩選欄位
 * @param filterData 使用的篩選條件
 * @returns 篩選過後的結果
 */
export const getUpdatedFilterItems = (
    initialFilterData: I_FilterItemList | null,
    showDataFilter: I_FilterItemList | null,
    filterData: any
) => {
    if (!initialFilterData || Object.keys(initialFilterData).length === 0) return null;
    if (!showDataFilter || Object.keys(showDataFilter).length === 0) return null;

    const updatedFilterItems = JSON.parse(JSON.stringify(initialFilterData)) as I_FilterItemList;

    // 處理 departureTimesRanges 和 crossday 以外的 disable
    Object.keys(initialFilterData)
        .filter(
            (key) => key !== 'departureTimesRanges' && key !== 'crossday' && key !== 'seqAirport'
        )
        .forEach((key: string) => {
            const checkedItems = filterData[key]?.flatMap((item: any) => item.value);

            initialFilterData[key].forEach((item: any, i: number) => {
                if (!showDataFilter[key].some((showItem: any) => showItem.value === item.value)) {
                    // 避開已經 checked 的選項不 disable
                    if (checkedItems && checkedItems.includes(updatedFilterItems[key][i].value))
                        return;

                    // 若新的 showData 篩出來的篩選條件沒有了，顯示的 filterItem 要 disable 掉
                    updatedFilterItems[key][i].disable = true;
                }
            });
        });

    // seqAirport 另外處理
    initialFilterData.seqAirport.forEach((item) => {
        const showDataTargetSeq = showDataFilter.seqAirport.find(
            ({ seqNum }) => seqNum === item.seqNum
        );

        const updatedSeqTarget = updatedFilterItems.seqAirport.find(
            ({ seqNum }) => seqNum === item.seqNum
        );

        if (!showDataTargetSeq || !updatedSeqTarget) return;

        const filterDataTargetSeq = filterData.seqAirport?.find(
            ({ seqNum }: { seqNum: number }) => seqNum === item.seqNum
        );
        const checkedSeqDep = filterDataTargetSeq?.departure.flatMap((item: any) => item.value);
        const checkedSeqArr = filterDataTargetSeq?.arrival.flatMap((item: any) => item.value);

        item.departure.forEach((depAirport, i) => {
            if (showDataTargetSeq.departure.some((showItem) => showItem.value === depAirport.value))
                return;

            // 避開已經 checked 的選項不 disable
            if (checkedSeqDep && checkedSeqDep.includes(updatedSeqTarget.departure[i].value))
                return;

            // 若新的 showData 篩出來的篩選條件沒有了，顯示的 filterItem 要 disable 掉
            updatedSeqTarget.departure[i].disable = true;
        });

        item.arrival.forEach((arrAirport, i) => {
            if (showDataTargetSeq.arrival.some((showItem) => showItem.value === arrAirport.value))
                return;

            // 避開已經 checked 的選項不 disable
            if (checkedSeqArr && checkedSeqArr.includes(updatedSeqTarget.arrival[i].value)) return;

            // 若新的 showData 篩出來的篩選條件沒有了，顯示的 filterItem 要 disable 掉
            updatedSeqTarget.arrival[i].disable = true;
        });
    });

    return updatedFilterItems;
};

/** @summary 取得篩選結果
 * @param filterData 篩選條件
 * @param searchData 原始搜尋結果
 * @returns 篩選過後的結果
 */
export const filterSearchResult = (filterData: I_SearchFilter, searchData: I_SearchPriceData) => {
    if (!searchData) return [];
    if (!filterData || Object.keys(filterData).length === 0) return searchData;
    const newSearchData = { ...searchData };

    const newFlightInfos = searchData.FlightInfos.filter((flightInfo: I_FlightInfos) => {
        let transfer_c = true;
        let airport_c = true;
        let dpTime_c = true;
        let seqAirports_c = true;
        let transferPoint_c = true;
        let Cabin_c = true;
        let crossday_c = true;
        let Gifts_c = true;

        // 根據轉機次數篩選: filterData.transfer
        if (filterData.transfer && filterData.transfer.length > 0) {
            transfer_c = false;
            filterData.transfer.forEach((transferFilter: I_Filter) => {
                // 直飛需要 全部航段都 transfer === 0
                // 轉機1次 只要其中一個  transfer === 1 || transfer < 2 (0,1)
                // 轉機2次 只要其中一個  transfer === 2 || transfer < 3 (0,1,2)
                // 以此類推..

                if (transferFilter.value === 0) {
                    if (
                        flightInfo.ItineraryInfos.every(
                            (c: I_ItineraryInfos) => c.Transfer === transferFilter.value
                        )
                    ) {
                        transfer_c = true;
                    }
                } else {
                    if (
                        flightInfo.ItineraryInfos.some(
                            (c: I_ItineraryInfos) => c.Transfer === transferFilter.value
                        ) &&
                        flightInfo.ItineraryInfos.every(
                            (c: I_ItineraryInfos) => c.Transfer <= Number(transferFilter.value)
                        )
                    ) {
                        transfer_c = true;
                    }
                }
            });
        }

        // 根據航空公司篩選: filterData.airport
        if (filterData.airport && filterData.airport.length > 0) {
            airport_c = false;
            filterData.airport.forEach((airportFilter: I_Filter) => {
                if (flightInfo.ValidatingAirline === airportFilter.value) {
                    airport_c = true;
                }
            });
        }

        // 根據是否為跨日航班篩選: filterData.crossday
        if (filterData.crossday && filterData.crossday.length > 0) {
            crossday_c = false;
            if (flightInfo.ItineraryInfos.every((c: I_ItineraryInfos) => c.CrossDay === 0)) {
                crossday_c = true;
            }
        }

        // 根據出發時間篩選: filterData.dpTimes
        if (filterData.dpTimes && filterData.dpTimes.length > 0) {
            dpTime_c = false;

            const checkArr = filterData.dpTimes.map((dpTime: string, i: number) => {
                // 如果 null 表示 預設值 要顯示全部的票卡
                if (dpTime === null) return true;

                // i 代表的是航段 i === 0 (去) / i === 1 (回) / i > 1 (多航段)
                if (flightInfo.ItineraryInfos[i]) {
                    const departTime = dayjs(flightInfo.ItineraryInfos[i].DepDateTime).format(
                        'HH:mm'
                    );
                    const times = dpTime.split('-');
                    const startTime = dayjs(dayjs().format('YYYY-MM-DD') + ' ' + times[0]).format(
                        'HH:mm'
                    );
                    const endTime = dayjs(dayjs().format('YYYY-MM-DD') + ' ' + times[1]).format(
                        'HH:mm'
                    );

                    if (
                        dayjs(dayjs().format('YYYY-MM-DD') + departTime).isSameOrAfter(
                            dayjs().format('YYYY-MM-DD') + startTime
                        ) &&
                        dayjs(dayjs().format('YYYY-MM-DD') + departTime).isSameOrBefore(
                            dayjs().format('YYYY-MM-DD') + endTime
                        )
                    ) {
                        return true;
                    }
                }
            });

            if (checkArr.every((filter) => filter === true)) dpTime_c = true;
        }

        // 根據航班出發 & 抵達機場篩選: filter.seqAirport
        if (filterData.seqAirport && filterData.seqAirport.length > 0) {
            seqAirports_c = false;

            const tempCheck: boolean[] = [];
            filterData.seqAirport.forEach((seq) => {
                const checkTargetItinerary = flightInfo.ItineraryInfos[seq.seqNum - 1];

                if (seq.departure.length > 0) {
                    tempCheck.push(
                        seq.departure
                            .flatMap((dep) => dep.value)
                            .includes(checkTargetItinerary.DepAirport)
                    );
                }

                if (seq.arrival.length > 0) {
                    tempCheck.push(
                        seq.arrival
                            .flatMap((arr) => arr.value)
                            .includes(checkTargetItinerary.ArrAirport)
                    );
                }
            });

            if (tempCheck.every((bool) => bool === true)) seqAirports_c = true;
        }

        // 根據轉機點篩選: filterData.transferPoint
        if (filterData.transferPoint && filterData.transferPoint.length > 0) {
            transferPoint_c = false;
            filterData.transferPoint.forEach((flight: I_Filter) => {
                flightInfo.ItineraryInfos.forEach((itinerary: I_ItineraryInfos) => {
                    if (
                        itinerary.SegmentInfos.some(
                            (segment: I_SegmentInfos) => segment.ArrAirport === flight.value
                        )
                    ) {
                        transferPoint_c = true;
                    }
                });
            });
        }

        // 根據禮品篩選: filterData.Gifts
        if (filterData.Gifts && filterData.Gifts.length > 0) {
            Gifts_c = false;

            filterData.Gifts.forEach((flight: I_Filter) => {
                // 只拿最低價方案做篩選顯示，其他方案不列入判斷
                const minPriceFare = flightInfo.FareInfos[0];
                if (Boolean(flight.value) === Boolean(minPriceFare.GiftNo)) {
                    Gifts_c = true;
                }

                // 過去 - 比對套裝方案中的所有方案
                // flightInfo.FareInfos.forEach((v: I_FareInfos) => {
                //     if (Boolean(flight.value) == Boolean(v.GiftNo)) {
                //         Gifts_c = true;
                //     }
                // });
            });
        }

        // 根據艙等篩選: filterData.Cabin
        if (filterData.Cabin && filterData.Cabin.length > 0) {
            Cabin_c = false;
            filterData.Cabin.forEach((flight: I_Filter) => {
                // 只拿最低價方案做篩選顯示，其他方案不列入判斷
                const minPriceFare = flightInfo.FareInfos[0];
                if (
                    minPriceFare.SegmentDetailInfos.some(
                        (segment: I_SegmentDetailInfos) => segment.Cabin === flight.value
                    )
                ) {
                    Cabin_c = true;
                }

                // 過去 - 比對套裝方案中的所有方案
                // flightInfo.FareInfos.forEach((fare: I_FareInfos) => {
                //     if (
                //         fare.SegmentDetailInfos.some(
                //             (segment: I_SegmentDetailInfos) => segment.Cabin === flight.value
                //         )
                //     ) {
                //         Cabin_c = true;
                //     }
                // });
            });
        }

        return (
            transfer_c &&
            airport_c &&
            transferPoint_c &&
            seqAirports_c &&
            Cabin_c &&
            dpTime_c &&
            crossday_c &&
            Gifts_c
        );
    });

    newSearchData.FlightInfos = newFlightInfos;
    return newSearchData;
};

/** @summary 比對兩個陣列是否一樣 (無視順序且只支持一維)
 * @param arr1
 * @param arr2
 * @returns boolean
 */
type arrType = number[] | string[];
export const isSameArr = (arr1: arrType, arr2: arrType): boolean => {
    if (arr1.length !== arr2.length) return false;

    const resultArr = [...arr2];

    for (const item of arr1) {
        const index = resultArr.indexOf(item);
        if (index === -1) break;
        resultArr.splice(index, 1);
    }

    return resultArr.length !== 0;
};

// ========================================================================================== 未使用

/** @summary 取得所有多選的欄位
 * @param filterData 篩選條件
 * @param searchData 初始搜索結果
 * @param multiSelect 多選的欄位名稱陣列 ['transfer','airport']
 * @returns 多選的篩選欄位
 */
/*
const getMultiSelectItems = (filterData: any, searchData: any, multiSelectArr: string[]) => {
    const noMultiSelectFilter: any = {} as I_SearchFilter;
    Object.keys(filterData)
        .filter((key) => !multiSelectArr.includes(key))
        .forEach((key) => {
            noMultiSelectFilter[key] = filterData[key];
        });

    const newShowDataFilter = filterSearchResult(
        noMultiSelectFilter,
        searchData
    ) as I_SearchPriceData;

    let multiItems: any = {};
    multiSelectArr.forEach((item) => {
        multiItems[item] = [];
    });

    let Airlines_lowestPrice: { [key: string]: number } = {};

    // 用每一筆的機票卡片資料做計算 (多選欄位判斷加在這邊)
    newShowDataFilter.FlightInfos.forEach((flight: I_FlightInfos) => {
        // 轉機次數 transfer
        flight.ItineraryInfos.forEach((itinerary) => {
            if (
                multiItems.transfer.filter((filter: any) => filter.value === itinerary.Transfer)
                    .length > 0
            )
                return;

            if (
                itinerary.Transfer > 0 &&
                flight.ItineraryInfos.every((filter) => filter.Transfer < itinerary.Transfer + 1)
            ) {
                multiItems.transfer.push({
                    text: '轉機' + itinerary.Transfer + '次',
                    value: itinerary.Transfer,
                });
            }

            if (
                itinerary.Transfer === 0 &&
                flight.ItineraryInfos.every((filter) => filter.Transfer === 0)
            ) {
                multiItems.transfer.push({
                    text: '直飛(含中停)',
                    value: 0,
                });
            }
        });

        // 航空聯盟、航空公司 airport
        flight.FareInfos.forEach((fare) => {
            if (!Airlines_lowestPrice[flight.ValidatingAirline]) {
                Airlines_lowestPrice[flight.ValidatingAirline] = fare.AdtPrice;
            }

            if (Airlines_lowestPrice[flight.ValidatingAirline] > fare.AdtPrice) {
                Airlines_lowestPrice[flight.ValidatingAirline] = fare.AdtPrice;
            }
        });
        if (
            multiItems.airport.filter((filter: any) => filter.value === flight.ValidatingAirline)
                .length === 0
        ) {
            multiItems.airport.push({
                text: flight.ValidatingAirlineName,
                value: flight.ValidatingAirline,
                price: Math.ceil(Airlines_lowestPrice[flight.ValidatingAirline]) || 0,
            });
        }
    });

    return multiItems;
};
*/

/** @summary 取得更新後的 filterData (用來清掉衝突的選項)
 * @param initialFilterData 初始篩選欄位 (API來的)
 * @param showDataFilter 當前結果計算出的篩選欄位
 * @param filterData 使用的篩選條件
 * @returns 更新後的 filterData
 */
/*
const getUpdatedFilterData = (
    initialFilterData: I_FilterItemList | null,
    showDataFilter: I_FilterItemList | null,
    filterData: any
) => {
    if (!initialFilterData || Object.keys(initialFilterData).length === 0) return;
    if (!showDataFilter || Object.keys(showDataFilter).length === 0) return;
    if (!filterData) return;

    const updatedFilterData = JSON.parse(JSON.stringify(filterData));

    // 處理 departureTimesRanges 和 crossday 以外的 disable
    Object.keys(initialFilterData)
        .filter((key) => key !== 'departureTimesRanges' && key !== 'crossday')
        .forEach((key: string) => {
            initialFilterData[key].forEach((item: any, i: number) => {
                if (!showDataFilter[key].some((showItem: any) => showItem.value === item.value)) {
                    // 若新的 showData 篩出來的篩選條件沒有了，則選擇到的篩選要取消
                    if (
                        updatedFilterData[key] &&
                        updatedFilterData[key].filter(
                            (c: I_Filter) => c.value === item.value && c.checked
                        ).length > 0
                    ) {
                        //找出原本的篩選條件
                        updatedFilterData[key] = updatedFilterData[key].filter(
                            (c: I_Filter) => c.value !== item.value
                        );
                    }
                }
            });
        });

    return updatedFilterData;
};
*/

/** @summary 檢查篩選條件是否只有 airport (尚未改成小時制)
 * @param filterData 篩選條件
 * @returns boolean
 */
/*
const checkOnlyAirport = (filterData: I_FilterItemList) => {
    if (!filterData) return;

    let onlyAirport = false;

    const unEmptyFilterItems: any = Object.keys(filterData).filter(
        (item) => filterData[item].length > 0
    );
    const includesAirport = unEmptyFilterItems.includes('airport');
    const includesDpTimes = unEmptyFilterItems.includes('dpTimes');
    const isAirport = unEmptyFilterItems[0] === 'airport';
    const dpTimesIsNull = unEmptyFilterItems['dpTimes']?.every((c: any) => c === null);

    // 若篩選條件只有航空公司 [airport] 的情況
    if (unEmptyFilterItems.length === 1 && isAirport) {
        onlyAirport = true;
    }

    // 若篩選有 [airport,dpTimes] 但 dpTimes 裡面都為 null,null
    if (unEmptyFilterItems.length === 2 && includesAirport && includesDpTimes && dpTimesIsNull) {
        onlyAirport = true;
    }

    return onlyAirport;
};
*/

/** @summary 根據 object 的某個 Key 判斷取得唯一陣列
 * @param data 陣列包物件的資料
 * @param key
 * @returns 唯一陣列
 */
/*
const getUniqueData = (data: any[], key: string) => {
    if (data.length === 0) return [];
    let uniqueData: any[] = [];
    data.forEach((item) => {
        if (!uniqueData.some((c: any) => c[key] === item[key])) {
            uniqueData.push(item);
        }
    });
    return uniqueData;
};
*/
